import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import RoleProtectedRoute from './components/RoleProtectedRoute';

// === Admin Pages ===
import AdminHome from './pages/admin/AdminHome';
import ManageUsers from './pages/admin/ManageUsers';

// === Staff Pages ===
import StaffHome from './pages/staff/StaffHome';
import Billing from './pages/staff/Billing';
import PettyCash from './pages/staff/PettyCash';

// === Student Pages ===
import StudentHome from './pages/student/StudentHome';
import Assignments from './pages/student/Assignments';
import Admission from './pages/staff/Admission';
import ManageStudents from './pages/staff/ManageStudents';
import ManageStaff from './pages/admin/ManageStaff';
import LeaveAndSalary from './pages/staff/LeaveAndSalary';
import ClientManagement from './pages/admin/ClientManagement';
import ClientDetails from './pages/admin/ClientDetails';
import SyllabusGenerator from './pages/admin/SyllabusGenerator';
import Leads from './pages/staff/Leads';
import LeadManagement from './pages/admin/LeadManagement';
import AdminTasks from './pages/admin/AdminTasks';
import MyTasks from './pages/staff/MyTasks';
import ExpenseTracker from './pages/admin/ExpenseTracker';
import AdminBatches from './pages/admin/AdminBatches';
import SendWhatsApp from './pages/admin/SendWhatsApp';
import AttendanceAdmin from './pages/admin/AttendanceAdmin';
import HiringBoard from './pages/staff/HiringBoard';
import ManageWorkshopRegistrations from './pages/admin/ManageWorkshopRegistrations';
import Interns from './pages/staff/Interns';

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
  }, []);

  // Default route redirection based on role
  const getDefaultRoute = (role) => {
    switch (role) {
      case 'admin':
        return '/dashboard/home';
      case 'staff':
        return '/dashboard/staff-home';
      case 'student':
        return '/dashboard/student-home';
      default:
        return '/';
    }
  };

  return (
    <Router>
      <Routes>
        {/* === LOGIN === */}
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={getDefaultRoute(user.role)} replace />
            ) : (
              <LoginPage setUser={setUser} />
            )
          }
        />

        {/* === DASHBOARD LAYOUT (Shared) === */}
        <Route
          path="/dashboard/*"
          element={
            user ? (
              <DashboardLayout user={user} setUser={setUser} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        >
          {/* ADMIN ROUTES */}
          <Route
            path="home"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <AdminHome />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-users"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <ManageUsers />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-staff"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <ManageStaff user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="clients"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <ClientManagement user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="clients/:clientId"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <ClientDetails />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="syllabus-generation"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <SyllabusGenerator />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="lead-management"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <LeadManagement user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="staff-attendance"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <AttendanceAdmin user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="tasks"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <AdminTasks user={user}/>
              </RoleProtectedRoute>
            }
          />
           <Route
            path="expense-tracker"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <ExpenseTracker user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-batches"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <AdminBatches user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="send-whatsapp"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin']}>
                <SendWhatsApp user={user}/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="workshop-registrations"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin','staff']}>
                <ManageWorkshopRegistrations user={user}/>
              </RoleProtectedRoute>
            }
          />
          {/* STAFF ROUTES */}
          <Route
            path="staff-home"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff']}>
                <StaffHome user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="Mytasks"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff', 'admin']}>
                <MyTasks user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="hiring"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff', 'admin']}>
                <HiringBoard user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="billing"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff']}>
                <Billing user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="petty-cash"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff']}>
                <PettyCash user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="admission"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff']}>
                <Admission user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="interns"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff']}>
                <Interns user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-students"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff', 'admin']}>
                <ManageStudents user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="leave-salary"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff']}>
                <LeaveAndSalary user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="leads"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['staff']}>
                <Leads user={user} />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="manage-batches"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['admin','staff']}>
                <AdminBatches user={user}/>
              </RoleProtectedRoute>
            }
          />
          {/* STUDENT ROUTES */}
          <Route
            path="student-home"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['student']}>
                <StudentHome />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="assignments"
            element={
              <RoleProtectedRoute user={user} allowedRoles={['student']}>
                <Assignments />
              </RoleProtectedRoute>
            }
          />
        </Route>

        {/* CATCH-ALL */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
